import { Link } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import BackToTop from "@/components/feature/BackToTop";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import FaqAccordion from "@/components/feature/FaqAccordion";
import JsonLd from "@/components/feature/JsonLd";
import { useSeo } from "@/hooks/useSeo";
import { faqs } from "@/mocks/faqs";

export default function Faq() {
  useSeo({
    title: "常见问题｜猎奇吃瓜 内容说明与投稿指南",
    description:
      "猎奇吃瓜常见问题解答：内容类型、悬案是否有结论、灵异投稿说明、更新频率、图文来源、转载授权、隐私与阅读提醒，一次说清。",
    keywords: "猎奇吃瓜常见问题,投稿指南,内容说明,悬案结论,转载授权",
    canonical: "/faq",
  });

  return (
    <div className="flex min-h-screen flex-col bg-background-950">
      <SiteHeader />
      <main className="flex-1">
        <section className="border-b border-background-800 bg-background-900/60">
          <div className="mx-auto w-full max-w-content px-4 py-10 md:px-6 md:py-14">
            <Breadcrumbs items={[{ label: "首页", to: "/" }, { label: "常见问题" }]} />
            <p className="mt-5 text-xs tracking-[0.3em] text-primary-400">FAQ</p>
            <h1 className="mt-3 font-heading text-3xl font-black text-foreground-50 md:text-4xl">常见问题</h1>
            <p className="mt-4 max-w-3xl text-sm leading-relaxed text-foreground-400 md:text-base">
              关于内容、投稿、转载与阅读提醒的说明都在这里。如果读完仍有疑问，欢迎直接写信给我们，高频问题会补充进本页。
            </p>
          </div>
        </section>

        <section className="mx-auto w-full max-w-content px-4 py-12 md:px-6 md:py-16">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-[minmax(0,1fr)_300px]">
            <div>
              <FaqAccordion items={faqs} defaultOpen={0} />
            </div>

            <aside className="lg:sticky lg:top-28 lg:self-start">
              <div className="rounded-lg border border-background-800 bg-background-900 p-5">
                <h2 className="flex items-center gap-2 font-heading text-base font-semibold text-foreground-50">
                  <i className="ri-links-line text-primary-400" /> 快速入口
                </h2>
                <div className="mt-4 flex flex-col gap-2">
                  <Link
                    to="/archives"
                    className="flex items-center justify-between whitespace-nowrap rounded-md bg-background-950 px-4 py-3 text-sm text-foreground-200 transition-colors hover:text-primary-300"
                  >
                    悬疑档案库 <i className="ri-arrow-right-line" />
                  </Link>
                  <Link
                    to="/archives?category=paranormal"
                    className="flex items-center justify-between whitespace-nowrap rounded-md bg-background-950 px-4 py-3 text-sm text-foreground-200 transition-colors hover:text-primary-300"
                  >
                    灵异杂谈投稿 <i className="ri-arrow-right-line" />
                  </Link>
                  <Link
                    to="/about"
                    className="flex items-center justify-between whitespace-nowrap rounded-md bg-background-950 px-4 py-3 text-sm text-foreground-200 transition-colors hover:text-primary-300"
                  >
                    关于我们 <i className="ri-arrow-right-line" />
                  </Link>
                </div>
              </div>

              <div className="mt-6 rounded-lg border border-accent-500/30 bg-accent-500/5 p-5">
                <h2 className="flex items-center gap-2 font-heading text-base font-semibold text-accent-200">
                  <i className="ri-alert-line" /> 阅读提醒
                </h2>
                <p className="mt-3 text-sm leading-relaxed text-foreground-400">
                  部分内容涉及失踪、暴力与超自然体验，可能引起不适。请根据自身状态选择阅读，不要深夜独自前往传闻地点探访。
                </p>
              </div>
            </aside>
          </div>

          <div className="mt-12 rounded-lg border border-background-800 bg-background-900 p-6 md:p-8">
            <h2 className="font-heading text-lg font-bold text-foreground-50">没有找到答案？</h2>
            <p className="mt-2 max-w-2xl text-sm leading-relaxed text-foreground-400">
              直接写信给我们，说明你的问题或线索。编辑部会在三个工作日内回复，并视情况把回答补充到本页。
            </p>
            <div className="mt-5 flex flex-wrap items-center gap-4">
              <span className="flex items-center gap-2 rounded-md border border-background-700 px-4 py-2.5 text-sm text-foreground-200">
                <i className="ri-mail-line text-primary-400" /> contact@lieqichigua.com
              </span>
              <Link
                to="/about"
                className="flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-950 transition-colors hover:bg-primary-400"
              >
                <i className="ri-information-line" /> 了解我们
              </Link>
            </div>
          </div>
        </section>
      </main>
      <SiteFooter />
      <BackToTop />

      <JsonLd
        id="faq-schema"
        data={{
          "@context": "https://schema.org",
          "@type": "FAQPage",
          url: "https://dioxy.cn/faq",
          inLanguage: "zh-CN",
          mainEntity: faqs.map((item) => ({
            "@type": "Question",
            name: item.question,
            acceptedAnswer: { "@type": "Answer", text: item.answer },
          })),
        }}
      />
      <JsonLd
        id="faq-breadcrumb"
        data={{
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "首页", item: "https://dioxy.cn/" },
            { "@type": "ListItem", position: 2, name: "常见问题", item: "https://dioxy.cn/faq" },
          ],
        }}
      />
    </div>
  );
}